import React, { useState, useEffect } from 'react';
import './EventFeed.css';
import PixelIcon from './PixelIcon';

function EventFeed({ events }) {
  const [selectedEvent, setSelectedEvent] = useState(null);

  const getCategoryColor = (category) => {
    const colors = {
      enforcement: 'cyan',
      military: 'red',
      social: 'yellow',
      policy: 'green',
      economic: 'magenta',
      tech: 'green'
    };
    return colors[category] || 'cyan';
  };
  
  const getCategoryIcon = (category) => {
    const icons = {
      enforcement: 'scale',
      military: 'sword',
      social: 'people',
      policy: 'clipboard',
      economic: 'coin',
      tech: 'chip'
    };
    return <PixelIcon name={icons[category] || 'doc'} size={14} />;
  };

  const handleEventClick = (event) => {
    if (event.rawText || event.impactScore !== undefined) {
      setSelectedEvent(event);
    }
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  useEffect(() => {
    if (!selectedEvent) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedEvent(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEvent]);

  return (
    <div className="event-feed-container">
      <h2 className="section-title glow-text">
        <span className="title-icon"><PixelIcon name="antenna" size={18} /></span>
        RECENT EVENTS & SIGNALS
      </h2>
      
      <div className="events-list">
        {events.map((event) => (
          <article
            key={event.id}
            className={`event-card pixel-border-thin ${event.impactScore > 0 ? 'toward-race' : event.impactScore < 0 ? 'toward-slowdown' : ''}`}
            onClick={() => handleEventClick(event)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(event); } }}
            role="button"
            tabIndex={0}
            aria-label={`${event.title}: open AI analysis`}
          >
            <div className="event-header">
              <div className="event-meta">
                <span className={`event-category text-${getCategoryColor(event.category)}`}>
                  {getCategoryIcon(event.category)} {event.category.toUpperCase()}
                </span>
                <span className="event-date text-secondary">{event.date}</span>
              </div>
              {event.sourceUrl ? (
                <a 
                  href={event.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="event-source event-source-link text-cyan"
                  onClick={(e) => e.stopPropagation()}
                >
                  {event.source} <PixelIcon name="link" size={10} />
                </a>
              ) : (
                <div className="event-source text-cyan">{event.source}</div>
              )}
            </div>
            
            <div className="event-content">
              <h3 className="event-title">{event.title}</h3>
              <p className="event-description text-secondary">{event.description}</p>
              {event.alsoReportedBy && event.alsoReportedBy.length > 0 && (
                <div className="event-also">
                  <span className="event-also-label">Also reported by</span>
                  {event.alsoReportedBy.map(r => (
                    <a
                      key={r.url || r.source}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-also-source"
                      title={r.title}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.source}
                    </a>
                  ))}
                </div>
              )}
              {(event.rawText || event.impactScore !== undefined) && (
                <div className="event-ai-badge">
                  <span className="ai-icon"><PixelIcon name="robot" size={12} /></span>
                  AI analysis →
                </div>
              )}
            </div>
            
            <div className="event-impact">
              <div className="impact-title">Impact</div>
              <div className="impact-tags">
                {Object.entries(event.impact).map(([dim, value]) => (
                  <div key={dim} className="impact-tag">
                    <span className="impact-dim">{dim.replace('_', ' ')}</span>
                    <span className={`impact-value ${value > 0 ? 'text-red' : 'text-green'}`}>
                      {value > 0 ? '+' : ''}{value.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Modal for AI Analysis Details */}
      {selectedEvent && (
        <div className="event-modal-overlay" onClick={closeModal}>
          <div className="event-modal pixel-border" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="AI analysis">
            <div className="modal-header">
              <h2 className="modal-title glow-text">
                <span className="ai-icon"><PixelIcon name="robot" size={16} /></span>
                AI ANALYSIS
              </h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close"><PixelIcon name="close" size={14} /></button>
            </div>
            
            <div className="modal-content">
              <div className="modal-section">
                <h3 className="modal-section-title text-cyan">Event Title</h3>
                <p className="text-primary">{selectedEvent.title}</p>
              </div>

              {selectedEvent.impactScore !== undefined && (
                <div className="modal-section">
                  <h3 className="modal-section-title text-magenta">Impact Score</h3>
                  <p className={`text-${selectedEvent.impactScore > 0 ? 'red' : 'green'}`}>
                    {selectedEvent.impactScore > 0 ? '+' : ''}{selectedEvent.impactScore.toFixed(2)}
                    <span className="text-secondary"> ({selectedEvent.impactScore > 0 ? 'Towards Race' : 'Towards Slowdown'})</span>
                  </p>
                </div>
              )}

              {selectedEvent.dimensionReference && (
                <div className="modal-section">
                  <h3 className="modal-section-title text-cyan">Primary Dimension</h3>
                  <p className="text-green">{selectedEvent.dimensionReference.replace('_', ' ').toUpperCase()}</p>
                </div>
              )}

              {selectedEvent.description && (
                <div className="modal-section">
                  <h3 className="modal-section-title text-magenta">AI Reasoning</h3>
                  <p className="text-secondary modal-reasoning">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.rawText && (
                <div className="modal-section">
                  <h3 className="modal-section-title text-cyan">Source Content (Preview)</h3>
                  <p className="text-secondary modal-raw-text">{selectedEvent.rawText}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventFeed;
